import React, { useState } from 'react';
import {
  X,
  Save,
  Loader,
  MapPin,
  Flag,
  Truck,
  Weight,
  Ruler
} from 'lucide-react';

export const TemplateModal = ({
  isOpen,
  onClose,
  onSave,
  missionData,
  loading = false
}) => {
  const [templateName, setTemplateName] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmed = templateName.trim();

    if (!trimmed) {
      setError('Le nom du template est obligatoire');
      return;
    }

    if (trimmed.length < 3) {
      setError('Le nom du template doit contenir au moins 3 caractères');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      await onSave(trimmed);
      setTemplateName('');
      onClose();
    } catch (err) {
      setError(err.message || 'Erreur lors de la sauvegarde du template');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setTemplateName('');
      setError('');
      onClose();
    }
  };

  if (!isOpen) return null;

  const trimmedLength = templateName.trim().length;

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
          zIndex: 50
        }}
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem'
        }}
      >
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '1rem',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            width: '100%',
            maxWidth: '28rem'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1.5rem',
              borderBottom: '1px solid #e5e7eb'
            }}
          >
            <div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>
                Sauvegarder comme template
              </h2>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
                Réutilisez cette configuration pour vos futures missions
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              style={{
                padding: '0.5rem',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.5 : 1
              }}
            >
              <X size={20} color="#6b7280" />
            </button>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label
                htmlFor="templateName"
                style={{ display: 'block', marginBottom: '0.5rem' }}
              >
                Nom du template <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                id="templateName"
                type="text"
                value={templateName}
                onChange={(e) => {
                  setTemplateName(e.target.value);
                  if (error) setError('');
                }}
                placeholder="Ex: Livraison entrepôt Paris"
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: error
                    ? '1px solid #ef4444'
                    : '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  backgroundColor: isSubmitting ? '#f9fafb' : 'white'
                }}
                maxLength={50}
              />
              {error && (
                <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  {error}
                </p>
              )}
            </div>

            {/* Preview */}
            <div
              style={{
                marginBottom: '1.5rem',
                padding: '1rem',
                backgroundColor: '#f9fafb',
                borderRadius: '0.5rem'
              }}
            >
              <h4 style={{ marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                Aperçu de la configuration
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                {missionData?.adresse_debut && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
                    <MapPin size={14} />
                    <span>Départ: {missionData.adresse_debut.label || missionData.adresse_debut}</span>
                  </div>
                )}
                {missionData?.adresse_fin && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
                    <Flag size={14} />
                    <span>Arrivée: {missionData.adresse_fin.label || missionData.adresse_fin}</span>
                  </div>
                )}
                {missionData?.type_transport && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
                    <Truck size={14} />
                    <span>Transport: {missionData.type_transport}</span>
                  </div>
                )}
                {missionData?.poids_total && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
                    <Weight size={14} />
                    <span>Poids: {missionData.poids_total} kg</span>
                  </div>
                )}
                {missionData?.kilometrage && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
                    <Ruler size={14} />
                    <span>Distance: {missionData.kilometrage} km</span>
                  </div>
                )}
              </div>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                style={{
                  flex: 1,
                  padding: '0.75rem 1rem',
                  border: '1px solid #d1d5db',
                  color: '#374151',
                  borderRadius: '0.5rem',
                  backgroundColor: 'white',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  opacity: isSubmitting ? 0.5 : 1
                }}
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isSubmitting || trimmedLength < 3}
                style={{
                  flex: 1,
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderRadius: '0.375rem',
                  backgroundColor: isSubmitting || trimmedLength < 3 ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  cursor: isSubmitting || trimmedLength < 3 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                {isSubmitting ? (
                  <>
                    <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    <span>Sauvegarde...</span>
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    <span>Sauvegarder</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Animation Spinner */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </>
  );
};
